import type { RefObject } from "react";
import { Link } from "wouter";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BrandEyebrowBadge } from "@/components/brand/brand-eyebrow-badge";
import { BidderCarGridSection } from "@/components/dashboard/BidderCarGridSection";
import { mapSpotlightToListingCards } from "./live-auction-home-cards";

type SpotlightRow = {
  car: {
    id: number;
    titleAr: string;
    year?: number | null;
    mileage?: number | null;
    fuelType?: string | null;
    location?: string | null;
    createdAt?: Date | string | null;
    mainImage?: string | null;
    saleType?: string | null;
  };
  auction: {
    id: number;
    currentBid: string | number;
    startingPrice?: string | number | null;
    currentBidder?: number | null;
    startTime: string | Date;
    endTime: string | Date;
  };
};

type Props = {
  liveAuctions: SpotlightRow[] | undefined;
  loading: boolean;
  timelineRef: RefObject<HTMLElement | null>;
};

export function LiveAuctionHomeSection({ liveAuctions, loading, timelineRef }: Props) {
  const cards = mapSpotlightToListingCards(liveAuctions ?? []);

  return (
    <section
      ref={timelineRef}
      className="relative w-full overflow-hidden bg-white py-16 md:py-24"
    >
      <div className="container relative">
        <div className="mb-10 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <BrandEyebrowBadge>مزادات</BrandEyebrowBadge>
            <h2 className="text-3xl font-bold tracking-tight text-neutral-900 md:text-4xl">
              مزادات نشطة الآن
            </h2>
            <p className="mt-2 max-w-xl text-sm text-neutral-500 md:text-base">
              تصفح السيارات المعروضة في المزاد وافتح البطاقة للتفاصيل
            </p>
          </div>
          <Button asChild variant="outline" className="shrink-0 rounded-xl">
            <Link href="/listings?type=auction">
              كل المزادات
              <ArrowRight className="size-4" />
            </Link>
          </Button>
        </div>

        {loading || cards.length > 0 ? (
          <BidderCarGridSection cars={loading ? undefined : cards} isLoading={loading} />
        ) : (
          <div className="rounded-2xl border border-dashed border-neutral-200 bg-neutral-50 px-6 py-14 text-center">
            <p className="text-sm text-neutral-500 md:text-base">
              لا توجد مزادات نشطة حالياً. تصفح قائمة المزادات للاطلاع على العروض القادمة.
            </p>
            <Button asChild variant="outline" className="mt-5 rounded-xl">
              <Link href="/listings?type=auction">تصفح قائمة المزادات</Link>
            </Button>
          </div>
        )}
      </div>
    </section>
  );
}
